import React, { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import {
  requestUnsubscribeNewsletter,
  useSubscriptionState,
  useSubscriptionDispatch,
} from "../../Context";
import { getErrorMessages } from "../../Utils";
import { Button } from "../../Components/Button";

const Unsubscribe = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const subscriptionIdParam = searchParams.get("id");

  const dispatch = useSubscriptionDispatch();

  const { unsubscribeStatus, unsubsribeLoading, unsubsribeError } =
    useSubscriptionState();

  useEffect(() => {
    if (subscriptionIdParam) {
      requestUnsubscribeNewsletter(dispatch, { id: subscriptionIdParam });
    }
  }, [dispatch, subscriptionIdParam]);

  useEffect(() => {
    if (unsubscribeStatus === "Success") {
      const timeoutId = setTimeout(() => {
        navigate("/dashboard");
      }, 2000);
      return () => clearTimeout(timeoutId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [unsubscribeStatus]);

  const renderSkeleton = () => (
    <div>
      <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full animate-pulse">
        <div className="h-8 w-8 bg-gray-300 rounded-full animate-pulse"></div>
      </div>
      <div className="mt-3 sm:mt-5 flex justify-center items-center flex-col">
        <div className="h-6 w-40 bg-gray-300 rounded animate-pulse"></div>
        <div className="mt-2">
          <div className="h-4 w-60 bg-gray-300 rounded animate-pulse"></div>
        </div>
      </div>
    </div>
  );

  return (
    <div className="max-w-lg mx-auto">
      <div className="bg-white p-8 mt-6 mb-0 space-y-4 rounded-lg shadow-sm text-center">
        {unsubsribeLoading ? (
          renderSkeleton()
        ) : (
          <div>
            {unsubscribeStatus === "Success" ? (
              <div>
                <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-green-100">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-6 w-6 text-green-600"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth="2"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M5 13l4 4L19 7"
                    />
                  </svg>
                </div>
                <div className="mt-3 sm:mt-5">
                  <h3 className="text-lg leading-6 font-medium text-gray-900">
                    {t("successfullyUnsubscribed")}
                  </h3>
                  <div className="mt-2">
                    <p className="text-sm text-gray-500">
                      {t("unsubscribeNewsletterMessage")}
                    </p>
                  </div>
                </div>
              </div>
            ) : (
              <div>
                <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-6 w-6 text-red-600"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth="2"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </div>
                <div className="mt-3 sm:mt-5">
                  <h3 className="text-lg leading-6 font-medium text-gray-900">
                    {t("Error")}
                  </h3>
                  <div className="mt-2">
                    <p className="text-sm text-gray-500">
                      {t(getErrorMessages(unsubsribeError).toString())}
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
        <div className="mt-5 sm:mt-6 flex gap-4 justify-center">
          {unsubsribeError && (
            <Button
              type="button"
              loading={unsubsribeLoading}
              onClick={() => {
                if (subscriptionIdParam) {
                  requestUnsubscribeNewsletter(dispatch, {
                    id: subscriptionIdParam,
                  });
                }
              }}
              title={t("retry")}
              outline
              className="flex-1"
            />
          )}

          <Button
            type="button"
            loading={unsubsribeLoading}
            disabled={unsubsribeLoading}
            onClick={() => navigate("/dashboard")}
            title={t("BackToDashboard")}
            className="flex-1"
          />
        </div>
      </div>
    </div>
  );
};

export default Unsubscribe;
